import React, { useState, useRef, useEffect, useMemo } from 'react';

import { Grid, SailingDayHeader, TemplateLinkForm } from '.';

import { Alert, Dropdown, Popup, TextButton, Tooltip } from '@spark-labs/avocado-ui';
import {
  BubbleDrawer,
  BubbleButton,
  BubbleAccordion,
  BubbleCheckbox,
  BubbleInput,
  BubbleOverlay,
  BubbleFormGroup,
  BubbleAlert,
} from '@go-client/components/BubbleUI';

import { Event, BareEvent } from '@go-shared/types/event';
import {
  populate as populateEvent,
  bare as bareEvent,
  PartialEvent,
  eventToPartialEvent,
} from '@go-client/lib/event';
import { bare as bareContent } from '@go-client/lib/content';
import { populateDayTemplate } from '@go-client/lib/api/template';
import { EvaluatedEventTemplate, EventTemplate } from '@go-shared/types/eventTemplate';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { StaffTemplate } from '@go-shared/types/staffTemplate';

import { Plan, VenueDisposition } from '@go-shared/types/plan';
import {
  CELL_HEIGHT_STEP,
  CELL_WIDTH_STEP,
  DEFAULT_CELL_HEIGHT,
  DEFAULT_CELL_WIDTH,
  GridLocationData,
  MAX_CELL_HEIGHT,
  MAX_CELL_WIDTH,
  MIN_CELL_HEIGHT,
  MIN_CELL_WIDTH,
} from './Grid';
import Filter, { FilterOption, CategoryFilterOption } from '@go-client/lib/filter';
import { timeToMinutes, isSameTime } from '@go-shared/lib/time';
import { GroupEvent } from '@go-shared/types/groupEvent';
import { getGroupEvents, populate as populateGroupEvent } from '@go-client/lib/api/groupEvent';
import { subscribeToProject } from '@go-client/lib/sse';

import { checkForStaffWarnings } from '@go-shared/lib/grid';
import { StaffWarning } from '@go-shared/types/staffAllocation';

import './DailyPlanner.scss';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { ClickAway } from '@go-client/components/UIkit/ClickAway/ClickAway';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { isTruthy } from '@go-shared/lib/utils';
import {
  BareSimpleSailing,
  BareSimpleSailingDay,
  SailingDayBundle,
} from '@go-shared/types/sailing';
import { isDayLocked } from '@go-client/lib/sailing';
import {
  unlinkTemplateFragments,
  linkTemplateFragments,
} from '@go-client/lib/api/templateFragment';
import { StrategicLinkFragment, LinkFragment } from '@go-shared/types/templateFragment';
import { BareTemplate } from '@go-shared/types/template';
import { FragmentBlockSync } from '@go-client/components/TemplateLinkForm/TemplateLinkForm';
import { getDayLocation } from '@go-shared/lib/sailing';
import TemplateAssociations from '@go-client/components/TemplateAssociations';
import { FilterList } from './FilterList';
import { notUndefined } from '@go-shared/lib/functional';
import { getErrorMessage } from '@go-shared/errors';
import Fuse from 'fuse.js';
import { getStorage, setStorage } from '@go-client/lib/storage';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import StaffConflictsPanel from './StaffConflictsPanel';
import { extractTemplateStatus } from '@go-client/lib/itinerary';
import CanvasEventForm, { CanvasEventFormMode } from './CanvasEventForm/CanvasEventForm';
import { evaluateMergedVars } from '@go-shared/lib/mergedVars';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { canEditCanvas, canPublishDay } from '@go-shared/lib/permissions';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import {
  CommonTextTranslation,
  ContentTemplateTranslation,
  EventTemplateTranslation,
  VenueTemplateTranslation,
} from '@go-shared/types/translation';
import CanvasContentForm, { CanvasContentFormMode } from './CanvasContentForm/CanvasContentForm';
import { BareContent, Content } from '@go-shared/types/content';
import DailySchedulePanel, { DailySchedulePanelRef } from './DailySchedulePanel/DailySchedulePanel';
import CanvasVenueForm from './CanvasVenueForm/CanvasVenueForm';
import { bare, bareSailingDay } from '@go-shared/lib/itinerary';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { isDayPublished } from '@go-shared/lib/plan';
import { ReserveEvent } from '@go-shared/types/reserve';
import { findSchedules } from '@go-client/lib/api/reservable';
import {
  getReservableEventsFromSchedules,
  bare as bareReserveSchedule,
} from '@go-shared/lib/reserve';
import { eventsAreSimilar } from '@go-shared/lib/event';

const DAILY_SCHEDULE_WIDTH_STEP = 1;
const DEFAULT_DAILY_SCHEDULE_WIDTH = 500;
const MIN_DAILY_SCHEDULE_WIDTH = 400;
const MAX_DAILY_SCHEDULE_WIDTH = 1000;

const STAFF_CONFLICTS_WIDTH_STEP = 1;
const DEFAULT_STAFF_CONFLICTS_WIDTH = 350;
const MIN_STAFF_CONFLICTS_WIDTH = 250;
const MAX_STAFF_CONFLICTS_WIDTH = 1000;

export interface DailyPlannerInfo {
  plan: Plan;
  template?: BareTemplate;
  dayBundle?: SailingDayBundle;
  nextSailingDayLink?: string;
  prevSailingDayLink?: string;
  pathToFeed?: string;
  eventTmpls: EventTemplate[];
  venueTmpls: VenueTemplate[];
  staffTmpls: StaffTemplate[];
  contentTmpls: BareContentTemplate[];
  eventTmplTransls: EventTemplateTranslation[];
  contentTmplTransls: ContentTemplateTranslation[];
  venueTmplTransls: VenueTemplateTranslation[];
  commonTextTransls: CommonTextTranslation[];
}

interface DailyPlannerPageParams {
  variant: 'velvet' | 'navy';
  plannerInfo?: DailyPlannerInfo;
  gridTitle?: string;
  hideGroupEvents?: boolean;
  hideReserveEvents?: boolean;
  hideDayHeader?: boolean;
  viewPublished?: boolean;
  toggleViewPublished?: () => void;
  onGenerateReport?: () => void;
  onGenerateSchedule?: () => Promise<unknown>;
  onPublishPlan?: () => Promise<unknown>;
  onCreateEvent: (event: BareEvent, applyStaffToPlan?: boolean) => Promise<BareEvent>;
  onUpdateEvent: (
    event: BareEvent,
    applyStaffToPlan?: boolean,
    projectUpdateKey?: string,
    transferToTargetDay?: string
  ) => Promise<unknown>;
  onDeleteEvent: (eventId: string) => Promise<unknown>;
  onUpdateVenueData: (venueData: VenueDisposition) => Promise<unknown>;
  onTemplateNameClick?: (blockSync: FragmentBlockSync) => void;
  onReloadPlan: (opt?: {
    includeLibraries?: boolean;
    includeTranslations?: boolean;
  }) => Promise<unknown>;
  onCreateTemplateClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onSailingDayNameClick: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onCreateContent: (content: BareContent) => Promise<unknown>;
  onUpdateContent: (content: BareContent, transferToTargetDay?: string) => Promise<unknown>;
  onDeleteContent: (contentId: string) => Promise<unknown>;
}

type DailyPlannerPageProps = DailyPlannerPageParams;

// Just so that we can filter events (the event's name is its template's title)
interface FilterableEvent extends Event {
  name: string;
}

// Mapping functions to categorize filters
function venueTypeMapping(v: VenueTemplate) {
  return v.types.map(vt => {
    return { _id: vt._id, name: vt.name };
  });
}

function segmentMapping(e: FilterableEvent) {
  return e.template.segment ? [e.template.segment] : [];
}

function levelMapping(e: FilterableEvent) {
  return e.template.levels;
}

function attributeMapping(e: FilterableEvent) {
  return e.template.attributes;
}

// Functions to get a subset of items from a list of filter options
function segmentFilters(filters: FilterOption[]) {
  return filters.filter(
    f => f.type === 'category' && (f as CategoryFilterOption).categoryName === 'Segment'
  );
}

function attributeFilters(filters: FilterOption[]) {
  return filters.filter(
    f => f.type === 'category' && (f as CategoryFilterOption).categoryName === 'Attribute'
  );
}

function levelFilters(filters: FilterOption[]) {
  return filters.filter(
    f => f.type === 'category' && (f as CategoryFilterOption).categoryName === 'Level'
  );
}

/**
 * Daily planner page
 *
 *
 */

export default function DailyPlanner(props: DailyPlannerPageProps) {
  const { user } = useUserInfo();
  const { activeProperty } = useActiveProperty();

  const { plannerInfo } = props;
  const storage = getStorage();
  const dayBundle = plannerInfo?.dayBundle;

  const optionsDropdownRef = useRef<Dropdown>(null);
  const schedulePanelRef = useRef<DailySchedulePanelRef>(null);

  const serverInfo = useServerInfo();
  const { languages, defaultLanguage, settings } = serverInfo;

  const [loading, setLoading] = useState(true);
  const [singleColMode, setSingleColMode] = useState(!!storage.canvasSingleColMode);

  const previewVars = false;

  const [groupEvents, setGroupEvents] = useState<GroupEvent[]>([]);
  const [reserveEvents, setReserveEvents] = useState<ReserveEvent[]>([]);

  const [eventFormMode, setEventFormMode] = useState<CanvasEventFormMode | undefined>(undefined);
  const [contentFormMode, setContentFormMode] = useState<CanvasContentFormMode | undefined>(
    undefined
  );

  const [showDailySchedulePanel, setShowDailySchedulePanel] = useState(false);
  const [showStaffConflictsPanel, setShowStaffConflictsPanel] = useState(false);
  const [actionsVisible, setActionsVisible] = useState(true);

  const [showVenueForm, setShowVenueForm] = useState<VenueTemplate | undefined>();

  const [showTemplateLinkForm, setShowTemplateLinkForm] = useState(false);
  const [showTemplateViewForm, setShowTemplateViewForm] = useState(false);

  const [appliedEventFilters, setAppliedEventFilters] = useState<FilterOption[]>([]);
  const [appliedVenueFilters, setAppliedVenueFilters] = useState<FilterOption[]>([]);

  const [filteredVenues, setFilteredVenues] = useState<VenueTemplate[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);

  const [projectUpdateKey, setProjectUpdateKey] = useState<string | undefined>(undefined);

  const [showReloadPopup, setShowReloadPopup] = useState(false);

  const [venueWarnings, setVenueWarnings] = useState<StaffWarning[]>([]);
  const [expandFilters, setExpandFilters] = useState(false);

  const [cellWidth, setCellWidth] = useState(storage.cellWidth || DEFAULT_CELL_WIDTH);
  const [cellHeight, setCellHeight] = useState(storage.cellHeight || DEFAULT_CELL_HEIGHT);
  const [dailyScheduleWidth, setDailyScheduleWidth] = useState(
    storage.dailyScheduleWidth || DEFAULT_DAILY_SCHEDULE_WIDTH
  );
  const [staffConflictsWidth, setStaffConflictsWidth] = useState(
    storage.staffConflictsWidth || DEFAULT_STAFF_CONFLICTS_WIDTH
  );

  const eventFilters = useRef<Filter<FilterableEvent> | undefined>(undefined);
  const venueFilters = useRef<Filter<VenueTemplate> | undefined>(undefined);

  const [vFilterSearchVal, setVFilterSearchVal] = useState('');

  useEffect(() => {
    refreshDailyPlanner().catch(console.error);
  }, [plannerInfo, previewVars]);

  useEffect(() => {
    if (loading) return;

    activateCanvasSubscription().catch(console.error);
  }, [dayBundle?.day._id, loading]);

  useEffect(() => {
    if (loading) return;

    setStorage('cellWidth', cellWidth);
    setStorage('cellHeight', cellHeight);
    setStorage('dailyScheduleWidth', dailyScheduleWidth);
    setStorage('staffConflictsWidth', staffConflictsWidth);
    setStorage('canvasSingleColMode', singleColMode);
  }, [cellWidth, cellHeight, dailyScheduleWidth, staffConflictsWidth, singleColMode, loading]);

  useEffect(() => {
    if (loading) return;

    setStorage(
      'canvasEventFilters',
      appliedEventFilters.filter(f => f.selected).map(aef => aef.id)
    );
    setStorage(
      'canvasVenueFilters',
      appliedVenueFilters.filter(f => f.selected).map(avf => avf.id)
    );
  }, [appliedEventFilters, appliedVenueFilters, loading]);

  async function refreshDailyPlanner() {
    if (!plannerInfo) return;

    const { plan, eventTmpls, venueTmpls, staffTmpls } = plannerInfo;

    // `populateEvent` may throw an error if the event template or venue template is not
    // found. Since that breaks  the canvas, we instead catch the exception and print the error
    const evts = plan.events
      .map(bareEvent => {
        try {
          const result = populateEvent(bareEvent, eventTmpls, venueTmpls, staffTmpls, plan.day);
          const [title] = evaluateMergedVars(
            result.template.title,
            undefined,
            languages.map(l => l.key),
            defaultLanguage.key,
            defaultLanguage.key,
            result.mergedVars
          );

          const [description] = evaluateMergedVars(
            result.template.description,
            undefined,
            languages.map(l => l.key),
            defaultLanguage.key,
            defaultLanguage.key,
            result.mergedVars
          );

          const newTemp: EvaluatedEventTemplate = {
            ...result.template,
          };

          newTemp._evaluatedTitle = title;
          newTemp._evaluatedDesc = description;

          result.template = newTemp;

          return result;
        } catch (e) {
          console.error(getErrorMessage(e));
        }
        return;
      })
      .filter(notUndefined);

    const bareGroupEvents = props.hideGroupEvents
      ? []
      : await getGroupEvents(
          plan.property._id,
          dayBundle?.day.date ?? undefined,
          dayBundle?.day.date ?? undefined
        );

    const reserveSchedules = props.hideReserveEvents ? [] : await findSchedules(plan.property._id);

    const reserveEventsByDate = getReservableEventsFromSchedules(
      [dayBundle?.dateIso ?? undefined].filter(notUndefined),
      reserveSchedules.map(bareReserveSchedule)
    );

    const groupEvts = bareGroupEvents.map(bareGroupEvent =>
      populateGroupEvent(bareGroupEvent, plan.property, venueTmpls)
    );

    // We add a `name` field to events so that they match the `Filterable` type
    // that's needed by `Filter`
    const filterableEvents = evts.map((e: Event) => {
      return { ...e, name: e.template.title };
    });

    const newEventFilters = new Filter(filterableEvents, [
      { name: 'Segment', mapping: segmentMapping },
      { name: 'Level', mapping: levelMapping },
      { name: 'Attribute', mapping: attributeMapping },
    ]);

    const vts = plannerInfo?.venueTmpls.filter(vt => !vt.archived);

    const newVenueFilters = new Filter(vts, [
      {
        name: 'Venue Type',
        mapping: venueTypeMapping,
      },
    ]);

    appliedEventFilters.filter(f => f.selected).forEach(f => newEventFilters.updateFilter(f));
    appliedVenueFilters.filter(f => f.selected).forEach(f => newVenueFilters.updateFilter(f));

    // update from local storage
    newEventFilters
      .getFiltersById(storage.canvasEventFilters || [])
      .map(f => ({ ...f, selected: true }))
      .forEach(f => newEventFilters.updateFilter(f));
    newVenueFilters
      .getFiltersById(storage.canvasVenueFilters || [])
      .map(f => ({ ...f, selected: true }))
      .forEach(f => newVenueFilters.updateFilter(f));

    let newFilteredEvents = newEventFilters.getSelectedItems();
    if (newFilteredEvents.length === 0) {
      newFilteredEvents = newEventFilters.getAllItems();
    }

    let newFilteredVenues = newVenueFilters.getSelectedItems();
    if (newFilteredVenues.length === 0) {
      newFilteredVenues = newVenueFilters.getAllItems();
    }

    setGroupEvents(groupEvts.filter(g => g.status === 'approved' || g.status === 'modified'));
    setReserveEvents(reserveEventsByDate[dayBundle?.dateIso ?? ''] || []);
    setAppliedEventFilters(newEventFilters.getFilters());
    setAppliedVenueFilters(newVenueFilters.getFilters());
    newFilteredVenues.sort((a, b) => a.order - b.order);
    setFilteredVenues(newFilteredVenues);
    setFilteredEvents(newFilteredEvents);
    setVenueWarnings(checkForStaffWarnings(evts, timeToMinutes(plan.dayStart)));
    setExpandFilters(false);
    setShowReloadPopup(false);
    setLoading(false);

    eventFilters.current = newEventFilters;
    venueFilters.current = newVenueFilters;
  }

  async function activateCanvasSubscription() {
    if (!plannerInfo) return;

    if (dayBundle) {
      const subscribed = subscribeToProject(
        dayBundle.dateIso.substring(0, 10),
        (key: string) => {
          console.log('key:', key);
          setProjectUpdateKey(key);
        },
        () => {
          setShowReloadPopup(true);
        }
      );

      if (!subscribed) {
        await Alert.error(
          'Your browser does not support Server Sent Events. ' +
            'You will not receive external project updates'
        );
      }
    }
  }

  function openEventFormForCreation(locationData?: GridLocationData) {
    if (!plannerInfo) return;

    const { plan } = plannerInfo;

    let newEvent: PartialEvent = {
      staffAllocations: [],
      startDay: plan.day,
      highlights: [],
    };

    if (locationData) {
      newEvent.venue = locationData.venue;
      newEvent.startTime = locationData.time;
    }

    setEventFormMode({ type: 'create', event: newEvent, initialPage: 0, gridLoc: locationData });
  }

  async function publishPlan() {
    if (!props.onPublishPlan) return;

    await props.onPublishPlan();

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  function handleVenueFilterChange(filter: FilterOption) {
    if (!venueFilters.current) return;

    venueFilters.current.updateFilter(filter);

    let fltrVenues = venueFilters.current.getSelectedItems();

    if (fltrVenues.length === 0) {
      fltrVenues = venueFilters.current.getAllItems();
    }

    setAppliedVenueFilters(venueFilters.current.getFilters());
    fltrVenues.sort((a, b) => a.order - b.order);
    setFilteredVenues(fltrVenues);
  }

  function handleEventFilterChange(filter: FilterOption) {
    if (!eventFilters.current) return;

    eventFilters.current.updateFilter(filter);

    let fltrEvts = eventFilters.current.getSelectedItems();

    if (fltrEvts.length === 0) {
      fltrEvts = eventFilters.current.getAllItems();
    }

    setAppliedEventFilters(eventFilters.current.getFilters());
    setFilteredEvents(fltrEvts);
  }

  function handleFilterDelete(name: string, filter: FilterOption) {
    if (!eventFilters.current || !venueFilters.current) return;

    switch (name) {
      case 'venue':
      case 'type':
        handleVenueFilterChange(filter);
        break;
      case 'segment':
      case 'level':
      case 'attribute':
        handleEventFilterChange(filter);
        break;
    }
  }

  function handleAllFilterDelete() {
    if (!eventFilters.current || !venueFilters.current) return;

    handleEventFilterChange({ ...eventFilters.current.getAllFilter(), selected: false });
    handleVenueFilterChange({ ...venueFilters.current.getAllFilter(), selected: false });
  }

  async function handleEventUpdate(event: Event, _oldEvent: Event) {
    let be = bareEvent(event);

    // optimistic update of events, assuming call went through OK
    const eventPosition = filteredEvents.findIndex(e => e._id === _oldEvent._id);

    if (eventPosition > -1) {
      const optimisticFilteredEvents = [...filteredEvents];
      optimisticFilteredEvents.splice(eventPosition, 1, event);
      setFilteredEvents(optimisticFilteredEvents);
    }

    if (
      event.venue._id === _oldEvent.venue._id &&
      isSameTime(event.startTime, _oldEvent.startTime) &&
      event.duration === _oldEvent.duration
    ) {
      // event was moved/resized to the same place, skip the movement
      return;
    }

    if (props.onUpdateEvent) {
      await props.onUpdateEvent(be, undefined, projectUpdateKey);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  function openEventFormForEdit(event: Event) {
    if (!plannerInfo) return;

    const { plan } = plannerInfo;

    let editedEvent = eventToPartialEvent(event, plan.dayStart);

    const newEvtFormMode: CanvasEventFormMode = {
      type: 'edit',
      event: editedEvent,
      initialPage: 1,
      dayTransfer: dayBundle
        ? {
            current: bareSailingDay(dayBundle.day),
            options: dayBundle.sailing.days.map(bareSailingDay),
          }
        : undefined,
    };

    setEventFormMode(newEvtFormMode);
  }

  function openContentFormForEdit(content: Content) {
    if (!plannerInfo) return;

    const newCntFormMode: CanvasContentFormMode = {
      type: 'edit',
      content,
      initialPage: 1,
      dayTransfer: dayBundle
        ? {
            current: bareSailingDay(dayBundle.day),
            options: dayBundle.sailing.days.map(bareSailingDay),
          }
        : undefined,
    };

    setContentFormMode(newCntFormMode);
  }

  async function handleToggleEventHideClick(event: Event) {
    let be = bareEvent(event);
    be.hidden = !be.hidden;

    if (props.onUpdateEvent) {
      await props.onUpdateEvent(be, undefined, projectUpdateKey);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleContentDelete(contentId: string) {
    if (props.onDeleteContent) {
      await props.onDeleteContent(contentId);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleContentUpdate(content: Content, transferToTargetDay?: string) {
    if (props.onUpdateContent) {
      await props.onUpdateContent(bareContent(content), transferToTargetDay);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleUpdateEvent(
    event: Event,
    applyStaffToPlan?: boolean,
    transferToTargetDay?: string
  ) {
    let be = bareEvent(event);

    if (props.onUpdateEvent) {
      await props.onUpdateEvent(be, applyStaffToPlan, projectUpdateKey, transferToTargetDay);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleCreateEventFromReserve(event: Event) {
    let be = bareEvent(event);

    if (props.onCreateEvent) {
      be = await props.onCreateEvent(be);
    }

    if (props.onReloadPlan) await props.onReloadPlan();
    return be;
  }

  function closeEventForm() {
    setEventFormMode(undefined);
  }

  function closeVenueForm() {
    setShowVenueForm(undefined);
  }

  async function handleContentFormSubmit(content: Content, transferToTargetDay?: string) {
    if (!contentFormMode) return;

    const bc = bareContent(content);
    const { type: modeType } = contentFormMode;

    switch (modeType) {
      case 'create':
        if (props.onCreateContent) {
          await props.onCreateContent(bc);
        }
        break;
      case 'edit':
        if (props.onUpdateContent) {
          await props.onUpdateContent(bc, transferToTargetDay);
        }
        break;
    }

    if (props.onReloadPlan) await props.onReloadPlan();

    setContentFormMode(undefined);
  }

  async function handleEventFormSubmit(
    event: Event,
    applyStaffToPlan?: boolean,
    transferToTargetDay?: string
  ) {
    if (!eventFormMode) return;

    let be = bareEvent(event);
    const { type: modeType } = eventFormMode;

    switch (modeType) {
      case 'create':
        if (props.onCreateEvent) {
          await props.onCreateEvent(be, applyStaffToPlan);
        }
        break;
      case 'edit':
        if (props.onUpdateEvent) {
          await props.onUpdateEvent(be, applyStaffToPlan, projectUpdateKey, transferToTargetDay);
        }
        break;
    }

    if (props.onReloadPlan) await props.onReloadPlan();

    closeEventForm();
  }

  async function handleEventDelete(eventId: string) {
    if (props.onDeleteEvent) await props.onDeleteEvent(eventId);
    if (props.onReloadPlan) await props.onReloadPlan();

    closeEventForm();
  }

  async function handleVenueFormSubmit(venueData: VenueDisposition) {
    if (props.onUpdateVenueData) await props.onUpdateVenueData(venueData);

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleOnUnlinkTemplateFragments(linkFragments: StrategicLinkFragment[]) {
    if (!plannerInfo) return;

    const { plan } = plannerInfo;

    await unlinkTemplateFragments(plan.property._id, linkFragments);

    if (props.onReloadPlan) await props.onReloadPlan();
  }

  async function handleOnFinishLinkTemplateFragments(linkFragments: LinkFragment[]) {
    if (!plannerInfo) return;

    const { plan } = plannerInfo;

    await linkTemplateFragments(plan.property._id, linkFragments);

    if (props.onReloadPlan) await props.onReloadPlan();

    setShowTemplateLinkForm(false);
  }

  function formatFilterLabel(
    f: FilterOption,
    all?: (f: FilterOption) => string,
    category?: (f: FilterOption) => string
  ) {
    switch (f.type) {
      case 'all':
        return all ? all(f) : f.label;
      case 'category':
        return category ? category(f) : f.label;
      default:
        return f.label;
    }
  }

  const { templateFragments, templateHistory, templateStatus } = extractTemplateStatus(
    dayBundle?.day
  );

  const userCanEdit = canEditCanvas(user);
  const dayIsLocked = isDayLocked(dayBundle?.day.locked ?? false);

  const eventsLocked =
    !userCanEdit || dayIsLocked || templateStatus.events === 'linked' || !!props.viewPublished;
  const venueLocked =
    !userCanEdit || dayIsLocked || templateStatus.venueData === 'linked' || !!props.viewPublished;

  let appliedFilters =
    appliedVenueFilters.filter(f => f.selected).length +
    appliedEventFilters.filter(f => f.selected).length;

  let levelLabel = getPropertyConfiguration(plannerInfo?.plan.property.type ?? 'sea', 'levelLabel');

  const fuse = useMemo(() => {
    return new Fuse(appliedVenueFilters, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['label'],
    });
  }, [filteredVenues]);

  const fuseSearchResults = useMemo(() => {
    if (!vFilterSearchVal) return undefined;
    return fuse.search(vFilterSearchVal).map(s => s.item);
  }, [fuse, vFilterSearchVal]);

  function handleResetCustomizations() {
    setCellHeight(DEFAULT_CELL_HEIGHT);
    setCellWidth(DEFAULT_CELL_WIDTH);
    setDailyScheduleWidth(DEFAULT_DAILY_SCHEDULE_WIDTH);
    setStaffConflictsWidth(DEFAULT_STAFF_CONFLICTS_WIDTH);
    setSingleColMode(false);
  }

  const isPublished =
    settings.PUBLISHER_ROLE !== 'inactive' ? isDayPublished(props.plannerInfo?.plan) : undefined;

  const filteredReserveEvts = reserveEvents.filter(
    item => props.plannerInfo?.plan.events.findIndex(e => eventsAreSimilar(e, item)) === -1
  );

  return (
    <>
      <div
        className={`SideMenu flex flex-col ${
          { navy: 'bg-navy-darkest', velvet: 'bg-velvet-darkest' }[props.variant]
        } shadow-md`}
      >
        <div className="bg-white flex flex-col items-center">
          <BrandLogo logo={activeProperty.brand.logo} />
          <PropertyBanner property={activeProperty} />
        </div>
        <div
          className={`flex flex-col relative items-stretch p-4 ${
            { navy: 'bg-navy-darker', velvet: 'bg-velvet-darker' }[props.variant]
          }`}
        >
          <div className="grid gap-2">
            {actionsVisible && (
              <>
                <div className="grid grid-cols-3 gap-1">
                  <BubbleButton
                    variant="white"
                    iconClasses={`text-${props.variant}`}
                    disabled={!plannerInfo?.prevSailingDayLink}
                    linkProps={{ to: plannerInfo?.prevSailingDayLink || '#' }}
                    block
                    icon="arrow-left"
                  />
                  <BubbleButton
                    variant="white"
                    iconClasses={`text-${props.variant}`}
                    disabled={!plannerInfo?.pathToFeed}
                    linkProps={{ to: plannerInfo?.pathToFeed || '#' }}
                    block
                    icon={['fass', 'newspaper']}
                  />
                  <BubbleButton
                    variant="white"
                    iconClasses={`text-${props.variant}`}
                    disabled={!plannerInfo?.nextSailingDayLink}
                    linkProps={{ to: plannerInfo?.nextSailingDayLink || '#' }}
                    block
                    icon="arrow-right"
                  />
                </div>
                {!isPublished && canPublishDay(user, serverInfo) && (
                  <BubbleButton
                    variant="emerald"
                    block
                    className="text-left"
                    disabled={eventsLocked}
                    onClick={() => {
                      publishPlan();
                    }}
                    icon="paper-plane"
                  >
                    Publish Draft
                  </BubbleButton>
                )}
                {!eventsLocked && (
                  <BubbleButton
                    variant="lime"
                    block
                    className="text-left"
                    onClick={() => {
                      openEventFormForCreation();
                    }}
                    icon="plus"
                  >
                    Add Event
                  </BubbleButton>
                )}
                {props.onGenerateSchedule && (
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    onClick={props.onGenerateSchedule}
                    icon="file-export"
                  >
                    Export Schedule
                  </BubbleButton>
                )}
                {props.onGenerateReport && (
                  <BubbleButton
                    variant="slate"
                    className="text-left"
                    block
                    onClick={props.onGenerateReport}
                    icon="user-tie"
                  >
                    View Staff Report
                  </BubbleButton>
                )}
                {venueWarnings.length !== 0 && (
                  <BubbleButton
                    className="text-left relative"
                    block
                    variant="banana"
                    onClick={() => setShowStaffConflictsPanel(!showStaffConflictsPanel)}
                    icon="triangle-exclamation"
                  >
                    Staff Conflicts
                  </BubbleButton>
                )}
              </>
            )}
            <BubbleButton
              inverse
              onClick={() => setActionsVisible(!actionsVisible)}
              variant="white"
              className="py-0-i"
              icon={actionsVisible ? 'caret-up' : 'caret-down'}
              iconClasses={`-ml-3 ${actionsVisible ? 'text-smoke-lightest' : 'text-white'}`}
            >
              <span className={actionsVisible ? 'text-smoke-lightest' : 'text-white'}>
                {actionsVisible ? 'Collapse' : 'Show'} Actions
              </span>
            </BubbleButton>
          </div>
        </div>
        <BubbleOverlay
          className="h-full"
          hideBg
          loaderProps={{ style: { fill: 'white' } }}
          loading={loading}
        >
          <BubbleAccordion
            variant={props.variant}
            className={`h-full flex flex-col border-t border-${props.variant}-darkest`}
            labels={['Event Filters', 'Venue Filters']}
            singleMode
            open={[1]}
          >
            <div className="grid p-4 gap-4">
              <BubbleFormGroup label="Segments" labelClassName="text-white-i">
                <div className="grid grid-cols-1 gap-1">
                  {segmentFilters(appliedEventFilters).map(f => (
                    <BubbleCheckbox
                      key={f.id}
                      label={formatFilterLabel(f, () => 'All Segments')}
                      className="text-white"
                      variant={props.variant}
                      checked={f.selected}
                      onValue={checked => handleEventFilterChange({ ...f, selected: checked })}
                    />
                  ))}
                </div>
              </BubbleFormGroup>
              <BubbleFormGroup label="Levels" labelClassName="text-white-i">
                <div className="grid grid-cols-1 gap-1">
                  {levelFilters(appliedEventFilters).map(f => (
                    <BubbleCheckbox
                      key={f.id}
                      label={formatFilterLabel(f, () => 'All Levels')}
                      className="text-white"
                      variant={props.variant}
                      checked={f.selected}
                      onValue={checked => handleEventFilterChange({ ...f, selected: checked })}
                    />
                  ))}
                </div>
              </BubbleFormGroup>
              <BubbleFormGroup label="Attributes" labelClassName="text-white-i">
                <div className="grid grid-cols-1 gap-1">
                  {attributeFilters(appliedEventFilters).map(f => (
                    <BubbleCheckbox
                      key={f.id}
                      label={formatFilterLabel(f, () => 'All Attributes')}
                      className="text-white"
                      variant={props.variant}
                      checked={f.selected}
                      onValue={checked => handleEventFilterChange({ ...f, selected: checked })}
                    />
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
            <div>
              <BubbleInput
                block
                type="text"
                variant={props.variant}
                className={`p-4 sticky top-0 bg-${props.variant}-darkest z-10`}
                value={vFilterSearchVal}
                placeholder="Search..."
                onValue={setVFilterSearchVal}
              />
              <div className="grid px-4 pb-4 grid-cols-1 gap-1">
                {(fuseSearchResults || appliedVenueFilters).map(f => (
                  <BubbleCheckbox
                    key={f.id}
                    label={formatFilterLabel(
                      f,
                      () => 'All Venues',
                      fl => `All ${fl.label}`
                    )}
                    className="text-white"
                    variant={props.variant}
                    checked={f.selected}
                    onValue={checked => handleVenueFilterChange({ ...f, selected: checked })}
                  />
                ))}
              </div>
            </div>
          </BubbleAccordion>
        </BubbleOverlay>
      </div>
      <div className="DailyPlannerContent flex-grow bg-blue-lightest flex flex-col">
        <div
          className="DailyPlannerHeader align-with-logo flex items-end
          sticky top-0 left-0 bg-blue-lightest"
        >
          <div className="absolute top-0 left-0">
            {plannerInfo && (
              <SailingDayHeader
                day={dayBundle?.day}
                dayLocked={dayBundle?.day?.locked ?? false}
                isPublished={isPublished}
                location={dayBundle ? getDayLocation(dayBundle.day) : undefined}
                date={dayBundle?.day.date}
                templateFragments={templateFragments}
                templateHistory={templateHistory}
                templateName={plannerInfo.template ? plannerInfo.template.name : undefined}
                onTemplateFragmentsClick={
                  dayBundle ? () => setShowTemplateLinkForm(true) : undefined
                }
                onTemplateNameClick={
                  plannerInfo.template ? () => setShowTemplateViewForm(true) : undefined
                }
              />
            )}
          </div>
          {appliedFilters > 0 && (
            <ClickAway
              onClickAway={() => setExpandFilters(false)}
              className={`DailyPlannerFilters ${expandFilters ? 'active' : ''}`}
            >
              <div className="filter-content">
                <FilterList
                  onFilterDelete={handleFilterDelete}
                  onAllFilterDelete={handleAllFilterDelete}
                  filters={[
                    {
                      name: 'venue',
                      color: '#7e8d9b',
                      filters: appliedVenueFilters.filter(f => f.type === 'item'),
                    },
                    {
                      name: 'type',
                      color: '#7e8d9b',
                      filters: appliedVenueFilters.filter(f => f.type === 'category'),
                    },
                    {
                      name: 'segment',
                      color: '#7e8d9b',
                      filters: segmentFilters(appliedEventFilters),
                    },
                    {
                      name: 'level',
                      color: '#7e8d9b',
                      filters: levelFilters(appliedEventFilters),
                    },
                    {
                      name: 'attribute',
                      color: '#7e8d9b',
                      filters: attributeFilters(appliedEventFilters),
                    },
                  ]}
                />
              </div>
              <div className="filter-cover" onClick={() => setExpandFilters(true)} />
            </ClickAway>
          )}
          {!expandFilters && isPublished === false && (
            <>
              {!props.viewPublished ? (
                <BubbleAlert
                  variant="dirt"
                  className="absolute bottom-0 left-0 z-10 rounded-0-i p-0-i py-1-i w-full cursor-pointer"
                  onClick={props.toggleViewPublished}
                >
                  You are viewing a draft version.
                  <span className="font-bold">Click here to view the published version.</span>
                </BubbleAlert>
              ) : (
                <BubbleAlert
                  variant="emerald"
                  className="absolute bottom-0 left-0 z-10 rounded-0-i p-0-i py-1-i w-full cursor-pointer"
                  onClick={props.toggleViewPublished}
                >
                  You are viewing the published version.{' '}
                  <span className="font-bold">
                    Click here to swap to the draft version to make changes.
                  </span>
                </BubbleAlert>
              )}
            </>
          )}
        </div>
        <div className="DailyPlannerGrid relative flex-grow">
          {!plannerInfo || loading ? (
            <BubbleOverlay loading className="w-full h-full" hideBg />
          ) : (
            <>
              <Grid
                singleColMode={singleColMode}
                gridLeftMargin={showStaffConflictsPanel ? staffConflictsWidth : 0}
                gridRightMargin={showDailySchedulePanel ? dailyScheduleWidth : 0}
                title={props.gridTitle}
                variant={props.variant}
                dayStart={plannerInfo.plan.dayStart}
                arrivalTimes={
                  dayBundle?.day.locations.map(l => l.arrival).filter(isTruthy) ?? undefined
                }
                departureTimes={
                  dayBundle?.day.locations.map(l => l.departure).filter(isTruthy) ?? undefined
                }
                plan={plannerInfo.plan}
                propertyType={plannerInfo.plan.property.type}
                venues={filteredVenues}
                events={filteredEvents}
                reserveEvents={filteredReserveEvts}
                groupEvents={groupEvents}
                eventsLocked={eventsLocked}
                levelLabel={levelLabel}
                cellWidth={cellWidth}
                cellHeight={cellHeight}
                eventTemplates={plannerInfo.eventTmpls}
                venueTemplates={plannerInfo.venueTmpls}
                contentTemplates={plannerInfo.contentTmpls}
                staffTemplates={plannerInfo.staffTmpls}
                venueLocked={venueLocked}
                onVenueClick={setShowVenueForm}
                onEventDelete={handleEventDelete}
                onEventMove={handleEventUpdate}
                onEventResize={handleEventUpdate}
                onEventDoubleClick={openEventFormForEdit}
                onLocationDoubleClick={openEventFormForCreation}
                onToggleEventHiddenClick={handleToggleEventHideClick}
                onStaffAllocationEditClick={openEventFormForEdit}
                onChangeCellWidth={(amt: number) => {
                  const newCellWidth = Math.max(
                    Math.min(cellWidth + amt, MAX_CELL_WIDTH),
                    MIN_CELL_WIDTH
                  );
                  setCellWidth(newCellWidth);
                }}
                onChangeCellHeight={(amt: number) => {
                  const newCellHeight = Math.max(
                    Math.min(cellHeight + amt, MAX_CELL_HEIGHT),
                    MIN_CELL_HEIGHT
                  );
                  setCellHeight(newCellHeight);
                }}
                onUpdateEvent={handleUpdateEvent}
                onCreateEventFromReserve={handleCreateEventFromReserve}
                afterGridSlot={
                  <div
                    className="sticky"
                    style={{
                      marginTop: 50,
                      bottom: 20,
                      left: 0,
                      zIndex: 10,
                    }}
                  >
                    <div className="flex items-stretch justify-center">
                      <Tooltip text="Single Column">
                        <BubbleButton
                          variant={props.variant}
                          className={`h-full rounded-right-0-i${
                            !singleColMode
                              ? ` border-b-4 border-${props.variant}-darker`
                              : ` border-b-2 bg-${props.variant}-lighter-i border-${props.variant}-darker`
                          }`}
                          icon="table-columns"
                          onClick={() => setSingleColMode(!singleColMode)}
                        />
                      </Tooltip>
                      <Tooltip text="View Options">
                        <div className="dropdown-inline-fix">
                          <Dropdown
                            ref={optionsDropdownRef}
                            maxHeight={1000}
                            alignTop
                            items={[{ key: 'item', element: 'item' }]}
                            renderFn={() => (
                              <div className="p-4">
                                <BubbleFormGroup label="Col Width">
                                  <BubbleInput
                                    min={MIN_CELL_WIDTH}
                                    max={MAX_CELL_WIDTH}
                                    step={CELL_WIDTH_STEP}
                                    block
                                    type="number"
                                    value={cellWidth}
                                    onValue={setCellWidth}
                                  />
                                  <BubbleInput
                                    min={MIN_CELL_WIDTH}
                                    max={MAX_CELL_WIDTH}
                                    step={CELL_WIDTH_STEP}
                                    block
                                    type="range"
                                    value={cellWidth}
                                    onValue={setCellWidth}
                                  />
                                </BubbleFormGroup>
                                <BubbleFormGroup label="Row Height">
                                  <BubbleInput
                                    min={MIN_CELL_HEIGHT}
                                    max={MAX_CELL_HEIGHT}
                                    step={CELL_HEIGHT_STEP}
                                    block
                                    type="number"
                                    value={cellHeight}
                                    onValue={setCellHeight}
                                  />
                                  <BubbleInput
                                    min={MIN_CELL_HEIGHT}
                                    max={MAX_CELL_HEIGHT}
                                    step={CELL_HEIGHT_STEP}
                                    block
                                    type="range"
                                    value={cellHeight}
                                    onValue={setCellHeight}
                                  />
                                </BubbleFormGroup>
                                <BubbleFormGroup label="Daily Schedule Width">
                                  <BubbleInput
                                    min={MIN_DAILY_SCHEDULE_WIDTH}
                                    max={MAX_DAILY_SCHEDULE_WIDTH}
                                    step={DAILY_SCHEDULE_WIDTH_STEP}
                                    block
                                    type="number"
                                    value={dailyScheduleWidth}
                                    onValue={setDailyScheduleWidth}
                                  />
                                  <BubbleInput
                                    min={MIN_DAILY_SCHEDULE_WIDTH}
                                    max={MAX_DAILY_SCHEDULE_WIDTH}
                                    step={DAILY_SCHEDULE_WIDTH_STEP}
                                    block
                                    type="range"
                                    value={dailyScheduleWidth}
                                    onValue={setDailyScheduleWidth}
                                  />
                                </BubbleFormGroup>
                                <BubbleFormGroup label="Staff Conflicts Width">
                                  <BubbleInput
                                    min={MIN_STAFF_CONFLICTS_WIDTH}
                                    max={MAX_STAFF_CONFLICTS_WIDTH}
                                    step={STAFF_CONFLICTS_WIDTH_STEP}
                                    block
                                    type="number"
                                    value={staffConflictsWidth}
                                    onValue={setStaffConflictsWidth}
                                  />
                                  <BubbleInput
                                    min={MIN_STAFF_CONFLICTS_WIDTH}
                                    max={MAX_STAFF_CONFLICTS_WIDTH}
                                    step={STAFF_CONFLICTS_WIDTH_STEP}
                                    block
                                    type="range"
                                    value={staffConflictsWidth}
                                    onValue={setStaffConflictsWidth}
                                  />
                                </BubbleFormGroup>
                                <BubbleButton
                                  block
                                  className="mt-3"
                                  variant="slate"
                                  onClick={() => handleResetCustomizations()}
                                  icon="rotate-left"
                                >
                                  Reset
                                </BubbleButton>
                                <BubbleButton
                                  className="absolute top-0 right-0"
                                  variant="navy"
                                  onClick={() =>
                                    optionsDropdownRef.current?.setState({ open: false })
                                  }
                                  inverse
                                  icon="times"
                                />
                              </div>
                            )}
                          >
                            {({ toggleRef, onBlur, onFocus, ...rest }) => (
                              <BubbleButton
                                className={`border-b-4 border-${props.variant}-darker rounded-left-0-i`}
                                buttonRef={toggleRef}
                                {...rest}
                                onClick={() =>
                                  optionsDropdownRef.current?.setState({
                                    open: !optionsDropdownRef.current?.state.open,
                                  })
                                }
                                variant={props.variant}
                                icon="gear"
                              />
                            )}
                          </Dropdown>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                }
              />
              <DailySchedulePanel
                ref={schedulePanelRef}
                width={dailyScheduleWidth}
                plan={plannerInfo.plan}
                groupEvts={groupEvents}
                reserveEvts={filteredReserveEvts}
                dayBundle={dayBundle}
                eventTemplates={plannerInfo.eventTmpls}
                venueTemplates={plannerInfo.venueTmpls}
                contentTemplates={plannerInfo.contentTmpls}
                staffTemplates={plannerInfo.staffTmpls}
                eventTemplateTranslations={plannerInfo.eventTmplTransls}
                contentTemplateTranslations={plannerInfo.contentTmplTransls}
                commonTextTranslations={plannerInfo.commonTextTransls}
                open={showDailySchedulePanel}
                onToggle={setShowDailySchedulePanel}
                onAddBtnClick={activeTab => {
                  switch (activeTab) {
                    case 'events':
                      setEventFormMode({
                        type: 'create',
                        event: {
                          startDay: plannerInfo.plan.day,
                          staffAllocations: [],
                          highlights: [],
                        },
                      });
                      break;
                    case 'content':
                      setContentFormMode({ type: 'create' });
                      break;
                  }
                }}
                onUpdateEvent={handleUpdateEvent}
                onCreateEvent={handleCreateEventFromReserve}
                onEditEvent={openEventFormForEdit}
                onRemoveEvent={handleEventDelete}
                onUpdateContent={handleContentUpdate}
                onEditContent={openContentFormForEdit}
                onRemoveContent={handleContentDelete}
                onUpdateVenue={handleVenueFormSubmit}
                venueTemplateTranslations={plannerInfo.venueTmplTransls}
              />
              <StaffConflictsPanel
                width={staffConflictsWidth}
                open={showStaffConflictsPanel}
                venueWarnings={venueWarnings}
                onSetShowStaffConflictsPanelClick={setShowStaffConflictsPanel}
              />
            </>
          )}
        </div>
      </div>
      {plannerInfo && contentFormMode && (
        <Popup hideHeader onClose={() => setContentFormMode(undefined)} position="full">
          <hr />
          <div style={{ width: '1000px' }}>
            <CanvasContentForm
              plan={plannerInfo.plan}
              mode={contentFormMode}
              contentTemplates={plannerInfo.contentTmpls.filter(c => !c.archived)}
              venueTemplates={plannerInfo.venueTmpls}
              contentTemplateTranslations={plannerInfo.contentTmplTransls}
              onCreateTemplate={() => {
                if (props.onReloadPlan) {
                  props.onReloadPlan({ includeLibraries: true });
                }
              }}
              onClose={() => setContentFormMode(undefined)}
              onSubmitContent={handleContentFormSubmit}
              onRemoveContent={id => {
                handleContentDelete(id);
                setContentFormMode(undefined);
                if (props.onReloadPlan) {
                  props.onReloadPlan();
                }
              }}
            />
          </div>
        </Popup>
      )}
      {plannerInfo && eventFormMode && (
        <Popup hideHeader onClose={closeEventForm} position="full">
          <hr />
          <div style={{ width: '1000px' }}>
            <CanvasEventForm
              mode={eventFormMode}
              plan={plannerInfo.plan}
              venueTemplates={plannerInfo.venueTmpls}
              eventTemplates={plannerInfo.eventTmpls}
              staffTemplates={plannerInfo.staffTmpls}
              eventTemplateTranslations={plannerInfo.eventTmplTransls}
              onCreateTemplate={() => {
                if (props.onReloadPlan) {
                  props.onReloadPlan({ includeLibraries: true });
                }
              }}
              onClose={closeEventForm}
              onSubmitEvent={handleEventFormSubmit}
              onRemoveEvent={handleEventDelete}
            />
          </div>
        </Popup>
      )}
      {plannerInfo && showVenueForm && (
        <div className="venue-panel">
          <Popup hideHeader onClose={closeVenueForm} title="Venue Details">
            <div style={{ width: '1000px' }}>
              <CanvasVenueForm
                selectedVenueTemplate={showVenueForm}
                setSelectedVenueTemplate={setShowVenueForm}
                plan={plannerInfo.plan}
                venueTemplates={plannerInfo.venueTmpls.filter(vt => !vt.archived)}
                onUpdateVenueData={handleVenueFormSubmit}
                onClose={closeVenueForm}
              />
            </div>
          </Popup>
        </div>
      )}
      {showReloadPopup && (
        <div
          className="absolute bg-white shadow"
          style={{
            bottom: 20,
            right: 20,
            height: 80,
            width: 200,
            zIndex: 99,
            paddingTop: 10,
            lineHeight: '30px',
            textAlign: 'center',
          }}
        >
          <div>
            The Canvas has changed. <TextButton onClick={refreshDailyPlanner}>Reload</TextButton>
          </div>
        </div>
      )}
      {plannerInfo && showTemplateLinkForm && (
        <BubbleDrawer
          onClose={() => setShowTemplateLinkForm(false)}
          containerClassName="bg-eggshell"
        >
          {dayBundle && (
            <TemplateLinkForm
              mode="single"
              user={user}
              property={plannerInfo.plan.property}
              sailing={bare(dayBundle.sailing)}
              sailingDay={bareSailingDay(dayBundle.day)}
              onClose={() => setShowTemplateLinkForm(false)}
              onTemplateNameClick={props.onTemplateNameClick}
              onUnlinkTemplateFragments={handleOnUnlinkTemplateFragments}
              onLinkTemplateFragments={handleOnFinishLinkTemplateFragments}
              onCreateTemplateClick={props.onCreateTemplateClick}
            />
          )}
        </BubbleDrawer>
      )}
      {plannerInfo && showTemplateViewForm && (
        <BubbleDrawer
          onClose={() => setShowTemplateViewForm(false)}
          containerClassName="bg-eggshell"
        >
          {plannerInfo.template && (
            <div className="p-8">
              <div className="flex items-start mb-2">
                <div>
                  <h1 className="text-xl text-slate-dark">Future Days Linked to Template</h1>
                </div>
                <BubbleButton
                  onClick={() => setShowTemplateViewForm(false)}
                  className="ml-auto -mt-3"
                  variant="navy"
                  inverse
                  icon="xmark-large"
                  iconSize="xl"
                />
              </div>
              <TemplateAssociations
                template={populateDayTemplate(plannerInfo.template, plannerInfo.plan.property)}
                onSailingDayNameClick={props.onSailingDayNameClick}
              />
            </div>
          )}
        </BubbleDrawer>
      )}
    </>
  );
}
